import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { useBootstrap, useTasaDia } from '../api/queries';
import {
  useBancos,
  useAnularCompra,
  useAnularPagoProveedor,
  useCompraDetalle,
  useCompras,
  useComprasNotas,
  useComprasPendientes,
  useCrearCompra,
  useCrearNotaCompra,
  usePagosProveedor,
  useProveedores,
  useRegistrarPagoProveedor,
} from '../api/queries-finanzas';
import { FinanceDemoNotice, TabBar } from '../components/FinanceBits';
import { Modal } from '../components/Modal';
import { SearchSelect } from '../components/SearchSelect';
import { StatusBadge } from '../components/Badges';
import { SortableTh } from '../components/SortableTh';
import { PlusIcon, TrashIcon } from '../components/icons';
import { useToast } from '../ui/ToastProvider';
import { addDaysIso, formatDate, formatMoney, todayIso } from '../lib/format';
import { useTableSort } from '../lib/useTableSort';
import type { Compra, CompraDetalle, NotaModalidad } from '../api/types';

const today = todayIso();
const in30 = addDaysIso(30);

interface CompraLinea {
  productoId: string;
  descripcion: string;
  cantidad: string;
  precio: string;
  exenta: boolean;
}

const EMPTY_LINE: CompraLinea = {
  productoId: '',
  descripcion: '',
  cantidad: '1',
  precio: '',
  exenta: false,
};

export function ComprasPage() {
  const { user, demo } = useAuth();
  const { notify } = useToast();
  const [view, setView] = useState<'facturas' | 'pagos' | 'notas'>('facturas');
  const [search, setSearch] = useState('');
  const [estado, setEstado] = useState('');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [page, setPage] = useState(1);
  const [openCompra, setOpenCompra] = useState(false);
  const [openPago, setOpenPago] = useState(false);
  const [pagoInicial, setPagoInicial] = useState<{
    proveedorId: string;
    compraId?: string;
  } | null>(null);
  const [detalleId, setDetalleId] = useState<string | null>(null);
  const [anulando, setAnulando] = useState<{
    tipo: 'compra' | 'pago';
    id: string;
    numero: string;
  } | null>(null);
  const [motivo, setMotivo] = useState('');
  const anularCompra = useAnularCompra();
  const anularPago = useAnularPagoProveedor();

  const compras = useCompras({
    search: search || undefined,
    estado: estado || undefined,
    desde: desde || undefined,
    hasta: hasta || undefined,
    page,
    limit: 25,
  });
  const pagos = usePagosProveedor();
  const [notaTipo, setNotaTipo] = useState<'' | 'CREDITO' | 'DEBITO'>('');
  const [notaDesde, setNotaDesde] = useState('');
  const [notaHasta, setNotaHasta] = useState('');
  const [notaPickOpen, setNotaPickOpen] = useState(false);
  const [notaPickSearch, setNotaPickSearch] = useState('');
  const [notaPick, setNotaPick] = useState<{ tipo: 'CREDITO' | 'DEBITO'; compraId: string } | null>(null);
  const notasCompra = useComprasNotas({
    tipo: notaTipo || undefined,
    desde: notaDesde || undefined,
    hasta: notaHasta || undefined,
  });
  const notaPickCompras = useCompras({
    search: notaPickSearch || undefined,
    page: 1,
    limit: 20,
  });
  const notaPickDetalle = useCompraDetalle(notaPick?.compraId ?? null);
  const rows = compras.data?.data ?? [];
  const { rows: sortedRows, sort, toggleSort } = useTableSort<
    Compra,
    'documento' | 'proveedor' | 'fecha' | 'base' | 'iva' | 'islr' | 'total' | 'saldo' | 'estado'
  >(
    rows,
    {
      documento: (row) => row.numeroInterno,
      proveedor: (row) => row.proveedorNombre,
      fecha: (row) => row.fechaEmision,
      base: (row) => row.subtotalVes,
      iva: (row) => row.impuestoVes,
      islr: (row) =>
        row.retencionIslrPrevistaVes ??
        (row.subtotalVes * (row.islrPct ?? row.proveedorIslrPct ?? 0)) / 100,
      total: (row) => row.totalVes,
      saldo: (row) => row.saldoVes,
      estado: (row) => row.estado,
    },
    'fecha',
    'desc',
  );
  const canCreate = !demo && user !== null && ['admin', 'supervisor'].includes(user.rol);
  const totalCompras = rows.reduce((sum, row) => sum + row.totalVes, 0);
  const totalSaldo = rows.reduce((sum, row) => sum + row.saldoVes, 0);
  const totalRetenciones = rows.reduce((sum, row) => {
    const aplicada = (row.retencionIslrUsd + row.retencionIvaUsd) * row.tasa;
    return (
      sum +
      (aplicada > 0.009
        ? aplicada
        : (row.retencionIslrPrevistaVes ?? 0) + (row.retencionIvaPrevistaVes ?? 0))
    );
  }, 0);
  const anulandoPending = anularCompra.isPending || anularPago.isPending;

  const confirmarAnulacion = async () => {
    if (!anulando) return;
    try {
      if (anulando.tipo === 'compra') {
        await anularCompra.mutateAsync({ id: anulando.id, motivo });
      } else {
        await anularPago.mutateAsync({ id: anulando.id, motivo });
      }
      notify(
        `${anulando.tipo === 'compra' ? 'Compra' : 'Pago'} ${anulando.numero} anulado con reverso.`,
        'success',
      );
      setAnulando(null);
      setMotivo('');
    } catch (error) {
      notify(error instanceof Error ? error.message : 'No se pudo anular.', 'error');
    }
  };

  return (
    <div className="dashboard">
      <header className="page-head">
        <div>
          <p className="eyebrow">Compras y proveedores</p>
          <h1 className="page-title">Compras & Pagos</h1>
          <p className="page-sub">
            IVA por línea, retenciones ISLR/IVA, entrada a inventario y asiento automático.
          </p>
        </div>
        <div className="page-head__actions">
          <button
            type="button"
            className="btn btn--outline"
            disabled={!canCreate}
            onClick={() => {
              setPagoInicial(null);
              setOpenPago(true);
            }}
          >
            Registrar pago
          </button>
          <button type="button" className="btn btn--primary" disabled={!canCreate} onClick={() => setOpenCompra(true)}>
            <PlusIcon size={17} /> Nueva compra
          </button>
        </div>
      </header>

      <FinanceDemoNotice demo={demo} />

      {view !== 'notas' ? (
      <div className="summary-strip">
        <div className="summary-tile">
          <span>Compras (página)</span>
          <strong>{formatMoney(totalCompras, 'VES')}</strong>
          <small>{rows.length} documentos</small>
        </div>
        <div className="summary-tile summary-tile--warning">
          <span>Saldo por pagar</span>
          <strong>{formatMoney(totalSaldo, 'VES')}</strong>
          <small>Facturas recibidas y parciales</small>
        </div>
        <div className="summary-tile summary-tile--accent">
          <span>Retenciones</span>
          <strong>{formatMoney(totalRetenciones, 'VES')}</strong>
          <small>Retenido o previsto en la página</small>
        </div>
      </div>
      ) : null}

      <div className="page-tabs-row">
        <TabBar
          label="Compras o pagos"
          value={view}
          onChange={(value) => {
            setView(value);
            setPage(1);
          }}
          tabs={[
            { value: 'facturas', label: 'Facturas de compra' },
            { value: 'pagos', label: 'Pagos a proveedores' },
            { value: 'notas', label: 'Notas de proveedor' },
          ]}
        />
      </div>

      {view === 'notas' ? (
        <section className="card transactions-card">
          <div className="transactions-filters">
            <div className="filter-controls">
              <select
                className="input"
                value={notaTipo}
                onChange={(event) => setNotaTipo(event.target.value as typeof notaTipo)}
                aria-label="Tipo de nota"
              >
                <option value="">Todas las notas</option>
                <option value="CREDITO">Notas de crédito</option>
                <option value="DEBITO">Notas de débito</option>
              </select>
              <input type="date" className="input input--date" value={notaDesde} onChange={(e) => setNotaDesde(e.target.value)} aria-label="Desde" />
              <input type="date" className="input input--date" value={notaHasta} onChange={(e) => setNotaHasta(e.target.value)} aria-label="Hasta" />
              <button type="button" className="btn btn--primary btn--sm" onClick={() => setNotaPickOpen(true)} disabled={!canCreate}>
                <PlusIcon size={15} /> Nueva nota
              </button>
            </div>
          </div>
          <div className="table-scroll">
            <table className="data-table data-table--compact">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Nota</th>
                  <th>Compra</th>
                  <th>Proveedor</th>
                  <th className="align-right">Total Bs.</th>
                  <th>Estado</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {notasCompra.isLoading ? (
                  <tr><td colSpan={7}><div className="table-empty">Cargando notas…</div></td></tr>
                ) : null}
                {!notasCompra.isLoading && (notasCompra.data?.data.length ?? 0) === 0 ? (
                  <tr><td colSpan={7}><div className="table-empty">Sin notas de proveedor en el período.</div></td></tr>
                ) : null}
                {(notasCompra.data?.data ?? []).map((nota) => (
                  <tr key={nota.id}>
                    <td>{formatDate(nota.fecha)}</td>
                    <td>
                      <div className="doc-cell">
                        <strong>{nota.numero}</strong>
                        <span>
                          {nota.tipo === 'CREDITO' ? 'Crédito' : 'Débito'}
                          {nota.modalidad === 'AJUSTE_PRECIO'
                            ? ' · ajuste de precio'
                            : nota.modalidad === 'DEVOLUCION'
                              ? ' · devolución'
                              : ' · cargo'}
                          {nota.numeroProveedor ? ` · prov. ${nota.numeroProveedor}` : ''}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="doc-cell">
                        <strong>{nota.numeroInterno}</strong>
                        <span>{nota.numeroFactura}</span>
                      </div>
                    </td>
                    <td>{nota.proveedorNombre}</td>
                    <td className="align-right">{formatMoney(nota.totalVes, 'VES')}</td>
                    <td><StatusBadge estado={nota.estado === 'emitida' ? 'emitida' : 'anulada'} /></td>
                    <td>
                      <div className="row-actions">
                        <button type="button" className="btn btn--ghost btn--sm" onClick={() => setDetalleId(nota.compraId)}>
                          Compra
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <footer className="table-footer">
            <span>
              {notasCompra.data?.totales.documentos ?? 0} notas ·{' '}
              {formatMoney(notasCompra.data?.totales.totalVes ?? 0, 'VES')}
            </span>
          </footer>
        </section>
      ) : view === 'facturas' ? (
        <section className="card transactions-card">
          <div className="transactions-filters">
            <div className="filter-tabs">
              {[
                ['', 'Todas'],
                ['RECIBIDA', 'Recibidas'],
                ['PARCIAL', 'Parciales'],
                ['PAGADA', 'Pagadas'],
                ['ANULADA', 'Anuladas'],
              ].map(([value, label]) => (
                <button
                  key={value || 'todas'}
                  type="button"
                  className={estado === value ? 'is-active' : undefined}
                  onClick={() => {
                    setEstado(value ?? '');
                    setPage(1);
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="filter-controls">
              <input type="date" className="input input--date" value={desde} onChange={(e) => setDesde(e.target.value)} aria-label="Desde" />
              <input type="date" className="input input--date" value={hasta} onChange={(e) => setHasta(e.target.value)} aria-label="Hasta" />
              <div className="search-input search-input--sm">
                <input
                  type="search"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  placeholder="Proveedor, factura o interno"
                  aria-label="Buscar compras"
                />
              </div>
            </div>
          </div>
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <SortableTh label="Documento" sortKey="documento" sort={sort} onSort={toggleSort} />
                  <SortableTh label="Proveedor / RIF" sortKey="proveedor" sort={sort} onSort={toggleSort} />
                  <SortableTh label="Fecha" sortKey="fecha" sort={sort} onSort={toggleSort} />
                  <SortableTh align="right" label="Base" sortKey="base" sort={sort} onSort={toggleSort} />
                  <SortableTh align="right" label="IVA" sortKey="iva" sort={sort} onSort={toggleSort} />
                  <SortableTh align="right" label="ISLR prev." sortKey="islr" sort={sort} onSort={toggleSort} />
                  <SortableTh align="right" label="Total" sortKey="total" sort={sort} onSort={toggleSort} />
                  <SortableTh align="right" label="Saldo" sortKey="saldo" sort={sort} onSort={toggleSort} />
                  <SortableTh label="Estado" sortKey="estado" sort={sort} onSort={toggleSort} />
                  <th />
                </tr>
              </thead>
              <tbody>
                {compras.isLoading ? <tr><td colSpan={10}><div className="table-empty">Cargando compras…</div></td></tr> : null}
                {!compras.isLoading && rows.length === 0 ? <tr><td colSpan={10}><div className="table-empty">Sin compras en este rango.</div></td></tr> : null}
                {sortedRows.map((row) => (
                  <tr
                    key={row.id}
                    className="row-clickable"
                    onClick={() => setDetalleId(row.id)}
                  >
                    <td>
                      <div className="doc-cell">
                        <strong>{row.numeroInterno}</strong>
                        <span>{row.numeroFactura} · {row.tipo}</span>
                      </div>
                    </td>
                    <td>
                      <div className="doc-cell">
                        <strong>{row.proveedorNombre}</strong>
                        <span>{row.proveedorRif}</span>
                      </div>
                    </td>
                    <td>{formatDate(row.fechaEmision)}</td>
                    <td className="align-right">{formatMoney(row.subtotalVes, 'VES')}</td>
                    <td className="align-right">{formatMoney(row.impuestoVes, 'VES')}</td>
                    <td className="align-right">
                      {formatMoney(
                        row.retencionIslrPrevistaVes ??
                          (row.subtotalVes * (row.islrPct ?? row.proveedorIslrPct ?? 0)) / 100,
                        'VES',
                      )}
                    </td>
                    <td className="align-right">
                      <div className="amount-cell">
                        <strong>{formatMoney(row.totalVes, 'VES')}</strong>
                      </div>
                    </td>
                    <td className="align-right">
                      <span className={row.saldoUsd > 0 ? 'saldo-pending' : 'saldo-ok'}>
                        {formatMoney(row.saldoVes, 'VES')}
                      </span>
                    </td>
                    <td><StatusBadge estado={row.estado} /></td>
                    <td>
                      <div className="row-actions">
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          onClick={(event) => {
                            event.stopPropagation();
                            setDetalleId(row.id);
                          }}
                        >
                          Detalle
                        </button>
                        {canCreate && row.estado !== 'ANULADA' ? (
                          <button
                            type="button"
                            className="btn btn--ghost btn--sm"
                            disabled={row.saldoUsd < row.totalUsd - 0.01}
                            title={
                              row.saldoUsd < row.totalUsd - 0.01
                                ? 'Anula primero los pagos aplicados'
                                : 'Anular compra'
                            }
                            onClick={(event) => {
                              event.stopPropagation();
                              setAnulando({ tipo: 'compra', id: row.id, numero: row.numeroInterno });
                              setMotivo('');
                            }}
                          >
                            Anular
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <footer className="table-footer">
            <span>Página {page} de {compras.data?.meta.pages ?? 1} · {compras.data?.meta.total ?? 0} compras</span>
            <div className="pager">
              <button type="button" className="btn btn--outline btn--sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Anterior</button>
              <button type="button" className="btn btn--outline btn--sm" disabled={page >= (compras.data?.meta.pages ?? 1)} onClick={() => setPage((p) => p + 1)}>Siguiente</button>
            </div>
          </footer>
        </section>
      ) : (
        <section className="card transactions-card">
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Pago</th>
                  <th>Proveedor</th>
                  <th>Fecha</th>
                  <th>Moneda</th>
                  <th>Monto</th>
                  <th>Aplicaciones</th>
                  <th>Estado</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {pagos.isLoading ? <tr><td colSpan={8}><div className="table-empty">Cargando pagos…</div></td></tr> : null}
                {!pagos.isLoading && (pagos.data ?? []).length === 0 ? <tr><td colSpan={8}><div className="table-empty">Sin pagos a proveedores.</div></td></tr> : null}
                {(pagos.data ?? []).map((pago) => (
                  <tr key={pago.id}>
                    <td>
                      <div className="doc-cell">
                        <strong>{pago.numero}</strong>
                        <span>{pago.referencia ?? 'Sin referencia'}</span>
                      </div>
                    </td>
                    <td>{pago.proveedorNombre}</td>
                    <td>{formatDate(pago.fecha)}</td>
                    <td>{pago.moneda}</td>
                    <td className="align-right">
                      <div className="amount-cell">
                        <strong>{formatMoney(pago.montoVes, 'VES')}</strong>
                        <span>{formatMoney(pago.montoVes, 'VES')}</span>
                      </div>
                    </td>
                    <td>{pago.aplicaciones}</td>
                    <td><StatusBadge estado={pago.estado === 'REGISTRADO' ? 'pagada' : 'anulada'} /></td>
                    <td>
                      {canCreate && pago.estado === 'REGISTRADO' ? (
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          onClick={() => {
                            setAnulando({ tipo: 'pago', id: pago.id, numero: pago.numero });
                            setMotivo('');
                          }}
                        >
                          Anular
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {detalleId ? (
        <CompraDetalleModal
          compraId={detalleId}
          onClose={() => setDetalleId(null)}
          onPagar={
            canCreate
              ? (proveedorId, compraId) => {
                  setDetalleId(null);
                  setPagoInicial({ proveedorId, compraId });
                  setOpenPago(true);
                }
              : undefined
          }
          canCreate={canCreate}
        />
      ) : null}

      {notaPickOpen ? (
        <Modal
          title="Nueva nota de proveedor"
          eyebrow="Selecciona la compra"
          onClose={() => setNotaPickOpen(false)}
          wide
        >
          <div className="modal-card__body">
            <div className="search-input">
              <input
                type="search"
                value={notaPickSearch}
                onChange={(event) => setNotaPickSearch(event.target.value)}
                placeholder="Buscar compra, proveedor o factura"
                aria-label="Buscar compra para la nota"
              />
            </div>
            <div className="simple-list">
              {(notaPickCompras.data?.data ?? []).map((compra) => (
                <li key={compra.id}>
                  <span>
                    {compra.numeroInterno} · {compra.numeroFactura} · {compra.proveedorNombre}
                  </span>
                  <div className="row-actions">
                    <strong>{formatMoney(compra.totalVes, 'VES')}</strong>
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      onClick={() => {
                        setNotaPick({ tipo: 'CREDITO', compraId: compra.id });
                        setNotaPickOpen(false);
                      }}
                    >
                      Crédito
                    </button>
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      onClick={() => {
                        setNotaPick({ tipo: 'DEBITO', compraId: compra.id });
                        setNotaPickOpen(false);
                      }}
                    >
                      Débito
                    </button>
                  </div>
                </li>
              ))}
            </div>
            {notaPickCompras.isLoading ? <p className="muted">Cargando compras…</p> : null}
            {!notaPickCompras.isLoading && (notaPickCompras.data?.data.length ?? 0) === 0 ? (
              <p className="muted">No hay compras que coincidan.</p>
            ) : null}
          </div>
        </Modal>
      ) : null}

      {notaPick && notaPickDetalle.data ? (
        <NotaCompraFormModal
          tipo={notaPick.tipo}
          compra={notaPickDetalle.data}
          onClose={() => setNotaPick(null)}
          onCreated={() => setNotaPick(null)}
        />
      ) : null}

      {openCompra ? (
        <CompraForm
          onClose={() => setOpenCompra(false)}
          onCreated={(numero) => {
            setOpenCompra(false);
            notify(`Compra ${numero} registrada, inventariada y contabilizada.`, 'success');
          }}
        />
      ) : null}
      {anulando ? (
        <Modal
          title={`Anular ${anulando.tipo} ${anulando.numero}`}
          eyebrow="Reverso contable"
          onClose={() => setAnulando(null)}
          footer={
            <>
              <button type="button" className="btn btn--outline" onClick={() => setAnulando(null)}>Cancelar</button>
              <button
                type="button"
                className="btn btn--danger"
                disabled={motivo.trim().length < 5 || anulandoPending}
                onClick={() => void confirmarAnulacion()}
              >
                {anulandoPending ? 'Anulando…' : 'Anular con reverso'}
              </button>
            </>
          }
        >
          <div className="modal-card__body">
            <label className="field">
              <span className="field-label">Motivo * (mínimo 5 caracteres)</span>
              <textarea
                className="input"
                rows={3}
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                placeholder="Ej.: factura duplicada del proveedor"
              />
            </label>
            <p className="inline-note">
              No se elimina información: se genera un asiento de reverso, se restituye el
              inventario y el banco cuando aplica, y el documento queda en estado anulado.
            </p>
          </div>
        </Modal>
      ) : null}
      {openPago ? (
        <PagoProveedorForm
          initialProveedorId={pagoInicial?.proveedorId}
          initialCompraId={pagoInicial?.compraId}
          onClose={() => setOpenPago(false)}
          onCreated={(numero) => {
            setOpenPago(false);
            notify(`Pago ${numero} registrado con retenciones.`, 'success');
          }}
        />
      ) : null}
    </div>
  );
}

function CompraDetalleModal({
  compraId,
  onClose,
  onPagar,
  canCreate,
}: {
  compraId: string;
  onClose: () => void;
  onPagar?: (proveedorId: string, compraId: string) => void;
  canCreate?: boolean;
}) {
  const { data, isLoading, isError, error } = useCompraDetalle(compraId);
  const [notaTipo, setNotaTipo] = useState<'CREDITO' | 'DEBITO' | null>(null);
  const islrRetenido = data ? data.retencionIslrUsd * data.tasa : 0;
  const ivaRetenido = data ? data.retencionIvaUsd * data.tasa : 0;
  const islrPrevisto = data?.retencionIslrPrevistaVes ?? 0;
  const ivaPrevisto = data?.retencionIvaPrevistaVes ?? 0;
  const hayRetencionAplicada = islrRetenido + ivaRetenido > 0.009;
  const islrEstimado = hayRetencionAplicada ? islrRetenido : islrPrevisto;
  const ivaEstimado = hayRetencionAplicada ? ivaRetenido : ivaPrevisto;
  const retenciones = islrEstimado + ivaEstimado;
  const netoEstimado = data ? data.totalVes - retenciones : 0;

  return (
    <Modal
      title={data?.numeroInterno ?? 'Detalle de compra'}
      eyebrow={data ? `${data.numeroFactura} · ${data.proveedorNombre}` : 'Compras y proveedores'}
      onClose={onClose}
      wide
      footer={
        <>
          <button type="button" className="btn btn--outline" onClick={onClose}>Cerrar</button>
          {canCreate && data && data.estado !== 'ANULADA' ? (
            <>
              <button type="button" className="btn btn--outline" onClick={() => setNotaTipo('CREDITO')}>
                Nota de crédito
              </button>
              <button type="button" className="btn btn--outline" onClick={() => setNotaTipo('DEBITO')}>
                Nota de débito
              </button>
            </>
          ) : null}
          {data && onPagar && data.estado !== 'PAGADA' && data.estado !== 'ANULADA' && data.saldoUsd > 0.009 ? (
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => onPagar(data.proveedorId, data.id)}
            >
              Registrar pago
            </button>
          ) : null}
        </>
      }
    >
      <div className="modal-card__body">
        {isLoading ? <p className="muted">Cargando detalle de la compra…</p> : null}
        {isError ? (
          <p className="inline-error">
            {error instanceof Error ? error.message : 'No se pudo cargar la compra.'}
          </p>
        ) : null}

        {data ? (
          <>
            <div className="summary-strip summary-strip--4">
              <div className="summary-tile">
                <span>Total compra</span>
                <strong>{formatMoney(data.totalVes, 'VES')}</strong>
                <small>{formatMoney(data.totalVes, 'VES')}</small>
              </div>
              <div className={`summary-tile ${data.saldoUsd > 0.01 ? 'summary-tile--warning' : 'summary-tile--accent'}`}>
                <span>Saldo</span>
                <strong>{formatMoney(data.saldoVes, 'VES')}</strong>
                <small>{data.saldoUsd > 0.01 ? 'Pendiente por pagar' : 'Compra pagada'}</small>
              </div>
              <div className="summary-tile">
                <span>Retenciones</span>
                <strong>{formatMoney(retenciones, 'VES')}</strong>
                <small>
                  {hayRetencionAplicada
                    ? 'ISLR + IVA retenidos en pagos'
                    : `Previstas: ISLR ${(data.islrPct ?? 0).toFixed(2)}% · IVA ${(data.retencionIvaPct ?? 0).toFixed(2)}%`}
                </small>
              </div>
              <div className="summary-tile">
                <span>Estado</span>
                <strong>{data.estado}</strong>
                <small>{data.tipo} · tasa {data.tasa} VES/USD</small>
              </div>
            </div>

            <div className="detail-grid">
              <div className="detail-block">
                <span>Proveedor</span>
                <strong>{data.proveedorNombre}</strong>
                <small>
                  {data.proveedorRif}
                  {data.proveedorIslrPct ? ` · ISLR ${data.proveedorIslrPct}%` : ''}
                </small>
              </div>
              <div className="detail-block">
                <span>Factura del proveedor</span>
                <strong>{data.numeroFactura}</strong>
                <small>{data.numeroInterno}</small>
              </div>
              <div className="detail-block">
                <span>Emisión</span>
                <strong>{formatDate(data.fechaEmision)}</strong>
                <small>
                  {data.fechaVencimiento ? `Vence ${formatDate(data.fechaVencimiento)}` : 'Sin vencimiento'}
                </small>
              </div>
              <div className="detail-block">
                <span>Condición</span>
                <strong>{data.exenta ? 'Compra exenta' : 'Compra con IVA'}</strong>
                <small>{data.tipo === 'SERVICIO' ? 'Servicio' : 'Artículo'}</small>
              </div>
            </div>

            <div>
              <h3 className="card-title">Ítems de la compra</h3>
              <div className="table-scroll">
                <table className="data-table data-table--compact">
                  <thead>
                    <tr>
                      <th>Descripción</th>
                      <th>Cuenta</th>
                      <th className="align-right">Cantidad</th>
                      <th className="align-right">Precio</th>
                      <th className="align-right">IVA</th>
                      <th className="align-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((item) => (
                      <tr key={item.id}>
                        <td>
                          <div className="doc-cell">
                            <strong>{item.descripcion}</strong>
                            <span>{item.exenta ? 'Exenta' : `IVA ${item.impuestoPct}%`}</span>
                          </div>
                        </td>
                        <td>{item.cuentaCodigo ? `${item.cuentaCodigo} · ${item.cuentaNombre}` : '—'}</td>
                        <td className="align-right">{item.cantidad.toLocaleString('es-VE')}</td>
                        <td className="align-right">{formatMoney(item.precioUnitarioUsd * data.tasa, 'VES')}</td>
                        <td className="align-right">{formatMoney(item.impuestoUsd * data.tasa, 'VES')}</td>
                        <td className="align-right"><strong>{formatMoney(item.totalUsd * data.tasa, 'VES')}</strong></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <dl className="detail-total">
              <div><dt>Base</dt><dd>{formatMoney(data.subtotalVes, 'VES')}</dd></div>
              <div><dt>IVA</dt><dd>{formatMoney(data.impuestoVes, 'VES')}</dd></div>
              <div>
                <dt>{hayRetencionAplicada ? 'ISLR retenido' : 'ISLR previsto'}</dt>
                <dd>{formatMoney(islrEstimado, 'VES')}</dd>
              </div>
              <div>
                <dt>{hayRetencionAplicada ? 'IVA retenido' : 'IVA ret. previsto'}</dt>
                <dd>{formatMoney(ivaEstimado, 'VES')}</dd>
              </div>
              <div><dt>Total Bs.</dt><dd>{formatMoney(data.totalVes, 'VES')}</dd></div>
              <div><dt>Neto a pagar</dt><dd>{formatMoney(netoEstimado, 'VES')}</dd></div>
            </dl>

            {data.pagos.length > 0 ? (
              <div>
                <h3 className="card-title">Pagos aplicados</h3>
                <ul className="simple-list">
                  {data.pagos.map((pago) => (
                    <li key={pago.id}>
                      <span>{pago.numero} · {formatDate(pago.fecha)}</span>
                      <strong>
                        {formatMoney(pago.montoAplicadoUsd * data.tasa, 'VES')}
                        {pago.retencionIslrUsd > 0
                          ? ` · ISLR ${formatMoney(pago.retencionIslrUsd * data.tasa, 'VES')}`
                          : ''}
                        {pago.retencionIvaUsd > 0
                          ? ` · IVA ${formatMoney(pago.retencionIvaUsd * data.tasa, 'VES')}`
                          : ''}
                      </strong>
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <p className="muted">Esta compra no tiene pagos aplicados.</p>
            )}

            {data.asiento ? (
              <div className="detail-block">
                <span>Asiento contable</span>
                <strong>N.º {data.asiento.numero} · {formatDate(data.asiento.fecha)}</strong>
                <small>
                  Debe {formatMoney(data.asiento.totalDebeUsd, 'USD')} · Haber{' '}
                  {formatMoney(data.asiento.totalHaberUsd, 'USD')} · {data.asiento.estado}
                </small>
              </div>
            ) : null}

            {(data.notas ?? []).length > 0 ? (
              <div>
                <h3 className="card-title">Notas del proveedor</h3>
                <ul className="simple-list">
                  {(data.notas ?? []).map((nota) => (
                    <li key={nota.id}>
                      <span>
                        {nota.tipo === 'CREDITO' ? 'Crédito' : 'Débito'}
                        {nota.modalidad === 'AJUSTE_PRECIO'
                          ? ' · ajuste de precio'
                          : nota.modalidad === 'DEVOLUCION'
                            ? ' · devolución'
                            : ' · cargo'}
                        {' '}{nota.numero}
                        {nota.numeroProveedor ? ` · prov. ${nota.numeroProveedor}` : ''} ·{' '}
                        {formatDate(nota.fecha)} · {nota.estado}
                      </span>
                      <strong>
                        {formatMoney(nota.totalVes, 'VES')}
                        {nota.saldoFavorUsd > 0
                          ? ` · favor ${formatMoney(nota.saldoFavorUsd * data.tasa, 'VES')}`
                          : ''}
                      </strong>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {data.observaciones ? (
              <div className="inline-note">
                <strong>Observaciones:</strong> {data.observaciones}
              </div>
            ) : null}

            <div className="inline-note">
              <strong>Compra recibida del proveedor.</strong> La imprenta TFHKA emite los
              documentos de venta de GRAVENCA y sus notas; esta factura de compra no se envía a
              TFHKA. Las notas del proveedor se registran con su número y ajustan la CxP y la
              contabilidad.
            </div>
          </>
        ) : null}
      </div>

      {notaTipo && data ? (
        <NotaCompraFormModal
          tipo={notaTipo}
          compra={data}
          onClose={() => setNotaTipo(null)}
          onCreated={() => setNotaTipo(null)}
        />
      ) : null}
    </Modal>
  );
}

function NotaCompraFormModal({
  tipo,
  compra,
  onClose,
  onCreated,
}: {
  tipo: 'CREDITO' | 'DEBITO';
  compra: CompraDetalle;
  onClose: () => void;
  onCreated: (numero: string) => void;
}) {
  const { notify } = useToast();
  const crear = useCrearNotaCompra();
  const esCredito = tipo === 'CREDITO';
  const [modalidad, setModalidad] = useState<NotaModalidad>(
    esCredito ? 'DEVOLUCION' : 'CARGO',
  );
  const [fecha, setFecha] = useState(today);
  const [motivo, setMotivo] = useState('');
  const [numeroProveedor, setNumeroProveedor] = useState('');
  const [devolverInventario, setDevolverInventario] = useState(true);
  const [seleccion, setSeleccion] = useState<
    Record<string, { checked: boolean; cantidad: string; precio: string }>
  >({});
  const [manual, setManual] = useState({
    descripcion: '',
    cantidad: '1',
    precio: '',
    exenta: false,
  });

  const esAjuste = modalidad === 'AJUSTE_PRECIO';
  const esDevolucion = modalidad === 'DEVOLUCION';
  const tasa = compra.tasa || 1;
  const precioCompra = (item: CompraDetalle['items'][number]) =>
    item.precioUnitarioUsd * tasa;
  const disponibleDe = (item: CompraDetalle['items'][number]) =>
    Math.max(0, item.cantidad - (item.devuelto ?? 0));
  const lineas = (compra.items ?? []).filter((item) =>
    esCredito && esDevolucion ? disponibleDe(item) > 0.0001 : true,
  );

  const preview = useMemo(() => {
    let base = 0;
    let iva = 0;
    const redondear = (value: number) => Math.round(value * 100) / 100;
    for (const item of compra.items ?? []) {
      const selected = seleccion[item.id];
      if (!selected?.checked) continue;
      const maximo = esCredito && esDevolucion ? disponibleDe(item) : item.cantidad;
      const qty = Math.min(Number(selected.cantidad) || 0, maximo);
      if (!(qty > 0)) continue;
      let lineaBase: number;
      if (esAjuste) {
        const original = precioCompra(item);
        const nuevo = Number(selected.precio) || 0;
        const delta = esCredito ? original - nuevo : nuevo - original;
        if (!(delta > 0)) continue;
        lineaBase = qty * delta;
      } else {
        lineaBase = qty * precioCompra(item);
      }
      base += lineaBase;
      iva += item.exenta ? 0 : lineaBase * (item.impuestoPct / 100);
    }
    if (modalidad === 'CARGO' && manual.descripcion.trim() && Number(manual.precio) > 0) {
      const lineaBase = Number(manual.cantidad || 0) * Number(manual.precio);
      base += lineaBase;
      iva += manual.exenta ? 0 : lineaBase * 0.16;
    }
    return { base: redondear(base), iva: redondear(iva), total: redondear(base + iva) };
  }, [compra.items, esAjuste, esCredito, modalidad, seleccion, manual, tasa]);

  const submit = async () => {
    if (motivo.trim().length < 5) {
      notify('El motivo debe tener al menos 5 caracteres.', 'warning');
      return;
    }
    const items: Array<Record<string, unknown>> = [];
    for (const item of compra.items ?? []) {
      const selected = seleccion[item.id];
      if (!selected?.checked) continue;
      const maximo = esCredito && esDevolucion ? disponibleDe(item) : item.cantidad;
      const cantidad = Math.min(Number(selected.cantidad) || 0, maximo);
      if (!(cantidad > 0)) continue;
      if (esAjuste) {
        const original = precioCompra(item);
        const nuevo = Number(selected.precio) || 0;
        const delta = esCredito ? original - nuevo : nuevo - original;
        if (!(delta > 0)) {
          notify(
            esCredito
              ? `El precio ajustado de "${item.descripcion}" debe ser menor al comprado.`
              : `El precio ajustado de "${item.descripcion}" debe ser mayor al comprado.`,
            'warning',
          );
          return;
        }
        items.push({ compraItemId: item.id, cantidad, precioAjustadoVes: nuevo });
      } else {
        items.push({ compraItemId: item.id, cantidad });
      }
    }
    if (modalidad === 'CARGO' && manual.descripcion.trim() && Number(manual.precio) > 0) {
      items.push({
        descripcion: manual.descripcion.trim(),
        cantidad: Number(manual.cantidad) || 1,
        precioUnitarioUsd: Number(manual.precio) / tasa,
        exenta: manual.exenta,
        impuestoPct: manual.exenta ? 0 : 16,
      });
    }
    if (items.length === 0) {
      notify('Selecciona al menos una línea para la nota.', 'warning');
      return;
    }
    try {
      const result = await crear.mutateAsync({
        id: compra.id,
        payload: {
          tipo,
          modalidad,
          fecha,
          motivo: motivo.trim(),
          ...(numeroProveedor.trim() ? { numeroProveedor: numeroProveedor.trim() } : {}),
          devolverInventario: esCredito && esDevolucion ? devolverInventario : false,
          idempotencyKey: crypto.randomUUID(),
          items,
        },
      });
      notify(
        `Nota ${result.data.numero} registrada con asiento #${result.data.asientoNumero}.`,
        'success',
      );
      onCreated(result.data.numero);
    } catch (error) {
      notify(error instanceof Error ? error.message : 'No se pudo registrar la nota.', 'error');
    }
  };

  const explicacion = esAjuste
    ? esCredito
      ? 'Ajuste de precio: el proveedor acredita la diferencia. Reduce la CxP y el costo/gasto, sin mover inventario.'
      : 'Ajuste de precio: el proveedor cobra la diferencia. Aumenta la CxP y el costo/gasto, sin mover inventario.'
    : esDevolucion
      ? 'Devolución al proveedor: reduce la CxP y el inventario/gasto. Si reingresas la mercancía, el inventario se rebaja.'
      : 'Cargo adicional del proveedor: aumenta la CxP y el gasto, sin mover inventario.';

  return (
    <Modal
      title={esCredito ? 'Nota de crédito del proveedor' : 'Nota de débito del proveedor'}
      eyebrow={`${compra.numeroInterno} · ${compra.numeroFactura}`}
      onClose={onClose}
      wide
      footer={
        <>
          <button type="button" className="btn btn--outline" onClick={onClose}>Cancelar</button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => void submit()}
            disabled={crear.isPending}
          >
            {crear.isPending ? 'Registrando…' : `Registrar nota · ${formatMoney(preview.total, 'VES')}`}
          </button>
        </>
      }
    >
      <div className="modal-card__body">
        <div className="segmented segmented--solid" role="group" aria-label="Modalidad de la nota">
          {esCredito ? (
            <button
              type="button"
              className={esDevolucion ? 'is-active' : ''}
              onClick={() => setModalidad('DEVOLUCION')}
            >
              Devolución
            </button>
          ) : (
            <button
              type="button"
              className={modalidad === 'CARGO' ? 'is-active' : ''}
              onClick={() => setModalidad('CARGO')}
            >
              Cargo adicional
            </button>
          )}
          <button
            type="button"
            className={esAjuste ? 'is-active' : ''}
            onClick={() => setModalidad('AJUSTE_PRECIO')}
          >
            Ajuste de precio
          </button>
        </div>

        <p className="inline-note">{explicacion}</p>

        <div className="form-grid form-grid--3">
          <label className="field">
            <span className="field-label">Fecha</span>
            <input className="input" type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} />
          </label>
          <label className="field">
            <span className="field-label">N.º del proveedor</span>
            <input
              className="input"
              value={numeroProveedor}
              onChange={(e) => setNumeroProveedor(e.target.value)}
              placeholder="Documento del proveedor"
            />
          </label>
          <label className="field field--wide">
            <span className="field-label">Motivo *</span>
            <input
              className="input"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Devolución, ajuste de precio, flete…"
            />
          </label>
        </div>

        <div className="line-items">
          <div className="line-items__head">
            <strong>Líneas de la compra</strong>
            <span className="pill pill--soft">
              {esAjuste ? 'Ajuste de precio' : esDevolucion ? 'Devolución' : 'Cargo'}
            </span>
          </div>
          <div className="table-scroll">
            <table className="data-table data-table--compact">
              <thead>
                <tr>
                  <th />
                  <th>Descripción</th>
                  <th className="align-right">Comprado</th>
                  <th className="align-right">{esAjuste ? 'Precio compra' : 'Disponible'}</th>
                  <th className="align-right">Cantidad</th>
                  <th className="align-right">{esAjuste ? 'Precio ajustado' : 'Precio'}</th>
                </tr>
              </thead>
              <tbody>
                {lineas.map((item) => {
                  const disponible = disponibleDe(item);
                  const state = seleccion[item.id] ?? {
                    checked: false,
                    cantidad: String(
                      esCredito && esDevolucion ? disponible : item.cantidad,
                    ),
                    precio: String(precioCompra(item)),
                  };
                  return (
                    <tr key={item.id}>
                      <td>
                        <input
                          type="checkbox"
                          checked={state.checked}
                          onChange={(e) =>
                            setSeleccion((current) => ({
                              ...current,
                              [item.id]: { ...state, checked: e.target.checked },
                            }))
                          }
                        />
                      </td>
                      <td>
                        <div className="doc-cell">
                          <strong>{item.descripcion}</strong>
                          <span>{item.cuentaCodigo ?? (item.exenta ? 'Exenta' : `IVA ${item.impuestoPct}%`)}</span>
                        </div>
                      </td>
                      <td className="align-right">{item.cantidad}</td>
                      <td className="align-right">
                        {esAjuste
                          ? formatMoney(precioCompra(item), 'VES')
                          : esDevolucion
                            ? disponible
                            : item.cantidad}
                      </td>
                      <td className="align-right">
                        <input
                          className="input input--qty"
                          type="number"
                          min="0.001"
                          max={esCredito && esDevolucion ? disponible : item.cantidad}
                          step="0.001"
                          value={state.cantidad}
                          disabled={!state.checked}
                          onChange={(e) =>
                            setSeleccion((current) => ({
                              ...current,
                              [item.id]: { ...state, cantidad: e.target.value },
                            }))
                          }
                        />
                      </td>
                      <td className="align-right">
                        {esAjuste ? (
                          <input
                            className="input input--qty"
                            type="number"
                            min="0"
                            step="0.01"
                            value={state.precio}
                            disabled={!state.checked}
                            onChange={(e) =>
                              setSeleccion((current) => ({
                                ...current,
                                [item.id]: { ...state, precio: e.target.value },
                              }))
                            }
                            aria-label={`Precio ajustado de ${item.descripcion}`}
                          />
                        ) : (
                          formatMoney(precioCompra(item), 'VES')
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {esCredito && esDevolucion && compra.tipo === 'ARTICULO' ? (
            <label className="checkbox">
              <input
                type="checkbox"
                checked={devolverInventario}
                onChange={(e) => setDevolverInventario(e.target.checked)}
              />
              Rebajar el inventario devuelto al proveedor
            </label>
          ) : null}
        </div>

        {modalidad === 'CARGO' ? (
          <div className="line-items">
            <div className="line-items__head"><strong>Concepto manual del proveedor</strong></div>
            <div className="form-grid form-grid--3">
              <label className="field field--wide">
                <span className="field-label">Descripción</span>
                <input
                  className="input"
                  value={manual.descripcion}
                  onChange={(e) => setManual({ ...manual, descripcion: e.target.value })}
                  placeholder="Flete, ajuste, servicio adicional…"
                />
              </label>
              <label className="field">
                <span className="field-label">Cantidad</span>
                <input className="input" type="number" min="0.001" value={manual.cantidad} onChange={(e) => setManual({ ...manual, cantidad: e.target.value })} />
              </label>
              <label className="field">
                <span className="field-label">Precio Bs.</span>
                <input className="input" type="number" min="0" value={manual.precio} onChange={(e) => setManual({ ...manual, precio: e.target.value })} />
              </label>
              <label className="checkbox">
                <input type="checkbox" checked={manual.exenta} onChange={(e) => setManual({ ...manual, exenta: e.target.checked })} />
                Exenta de IVA
              </label>
            </div>
          </div>
        ) : null}

        <div className="note-totals">
          <div><span>Base</span><strong>{formatMoney(preview.base, 'VES')}</strong></div>
          <div><span>IVA</span><strong>{formatMoney(preview.iva, 'VES')}</strong></div>
          <div className="note-totals__grand"><span>Total de la nota</span><strong>{formatMoney(preview.total, 'VES')}</strong></div>
        </div>
        <p className="page-footnote">
          La nota ajusta la CxP y se contabiliza contra inventario/gasto e IVA crédito. Si el
          proveedor ya cobró, el excedente queda como anticipo a proveedores.
        </p>
      </div>
    </Modal>
  );
}

function CompraForm({ onClose, onCreated }: { onClose: () => void; onCreated: (numero: string) => void }) {
  const { data: bootstrap } = useBootstrap();
  const { data: proveedores } = useProveedores('');
  const { notify } = useToast();
  const crear = useCrearCompra();
  const [proveedorId, setProveedorId] = useState('');
  const [numeroFactura, setNumeroFactura] = useState('');
  const [fecha, setFecha] = useState(today);
  const [vencimiento, setVencimiento] = useState(in30);
  const [tipo, setTipo] = useState<'SERVICIO' | 'ARTICULO'>('ARTICULO');
  const [tasa, setTasa] = useState(String(bootstrap?.tasas.find((t) => t.moneda === 'USD')?.tasa ?? 36.42));
  const [exenta, setExenta] = useState(false);
  const [islrPctInput, setIslrPctInput] = useState<string | null>(null);
  const [ivaRetPct, setIvaRetPct] = useState('0');
  const tasaDia = useTasaDia(fecha);

  useEffect(() => {
    const rate = tasaDia.data?.data.find((item) => item.moneda === 'USD');
    if (rate) setTasa(String(rate.tasa));
  }, [tasaDia.data]);
  const [lineas, setLineas] = useState<CompraLinea[]>([{ ...EMPTY_LINE }]);

  const productos = bootstrap?.productos ?? [];
  const updateLine = (index: number, patch: Partial<CompraLinea>) =>
    setLineas((current) => current.map((line, i) => (i === index ? { ...line, ...patch } : line)));
  const selectProduct = (index: number, productoId: string) => {
    const producto = productos.find((p) => p.id === productoId);
    updateLine(index, {
      productoId,
      descripcion: producto?.nombre ?? '',
      precio: producto
        ? String(Math.round(producto.precioUsd * 0.65 * (Number(tasa) || 1) * 100) / 100)
        : '',
      exenta: producto ? producto.impuestoPct === 0 : false,
    });
  };

  const totals = useMemo(() => {
    let base = 0;
    let tax = 0;
    for (const line of lineas) {
      const subtotal = (Number(line.cantidad) || 0) * (Number(line.precio) || 0);
      base += subtotal;
      if (!line.exenta && !exenta) tax += subtotal * 0.16;
    }
    return {
      base: Math.round(base * 100) / 100,
      tax: Math.round(tax * 100) / 100,
      total: Math.round((base + tax) * 100) / 100,
    };
  }, [lineas, exenta]);
  const proveedorSeleccionado = (proveedores?.data ?? []).find((p) => p.id === proveedorId);
  const islrPct = Number(islrPctInput ?? proveedorSeleccionado?.islrPct ?? 0);
  const islrEstimado = Math.round((totals.base * islrPct) / 100 * 100) / 100;
  const ivaRetPctNum = Number(ivaRetPct) || 0;
  const ivaRetenido = Math.round((totals.tax * ivaRetPctNum) / 100 * 100) / 100;
  const netoAPagar = Math.round((totals.total - islrEstimado - ivaRetenido) * 100) / 100;

  const submit = async () => {
    if (!proveedorId || !numeroFactura.trim()) {
      notify('Proveedor y número de factura son obligatorios.', 'warning');
      return;
    }
    const items = lineas
      .filter((line) => Number(line.cantidad) > 0 && Number(line.precio) >= 0)
      .map((line) => ({
        ...(line.productoId ? { productoId: line.productoId } : {}),
        descripcion: line.descripcion || 'Ítem de compra',
        cantidad: Number(line.cantidad),
        precioUnitarioUsd: (Number(line.precio) || 0) / (Number(tasa) || 1),
        exenta: line.exenta || exenta,
        impuestoPct: line.exenta || exenta ? 0 : 16,
      }));
    if (items.length === 0) {
      notify('Agrega al menos una línea válida.', 'warning');
      return;
    }
    try {
      const result = await crear.mutateAsync({
        proveedorId,
        numeroFactura: numeroFactura.trim(),
        tipo,
        fechaEmision: fecha,
        fechaVencimiento: vencimiento,
        tasa: Number(tasa),
        exenta,
        islrPct,
        retencionIvaPct: ivaRetPctNum,
        idempotencyKey: crypto.randomUUID(),
        items,
      });
      onCreated(result.data.numeroInterno);
    } catch (error) {
      notify(error instanceof Error ? error.message : 'No se pudo registrar la compra.', 'error');
    }
  };

  return (
    <Modal
      title="Nueva factura de compra"
      eyebrow="Compras"
      onClose={onClose}
      wide
      footer={
        <>
          <button type="button" className="btn btn--outline" onClick={onClose}>Cancelar</button>
          <button type="button" className="btn btn--primary" onClick={() => void submit()} disabled={crear.isPending}>
            {crear.isPending ? 'Registrando…' : 'Registrar y contabilizar'}
          </button>
        </>
      }
    >
      <div className="modal-card__body">
        <div className="form-grid form-grid--3">
          <label className="field">
            <span className="field-label">Proveedor *</span>
            <SearchSelect
              value={proveedorId}
              onChange={(value) => {
                setProveedorId(value);
                setIslrPctInput(null);
              }}
              options={(proveedores?.data ?? []).map((proveedor) => ({
                value: proveedor.id,
                label: proveedor.nombre,
                hint: proveedor.rif,
              }))}
              placeholder="Buscar proveedor por nombre o RIF"
              ariaLabel="Proveedor"
            />
          </label>
          <label className="field">
            <span className="field-label">Número de factura *</span>
            <input className="input" value={numeroFactura} onChange={(e) => setNumeroFactura(e.target.value)} />
          </label>
          <label className="field">
            <span className="field-label">Tipo</span>
            <select className="input" value={tipo} onChange={(e) => setTipo(e.target.value as 'SERVICIO' | 'ARTICULO')}>
              <option value="ARTICULO">Artículos</option>
              <option value="SERVICIO">Servicios</option>
            </select>
            <span className="field-hint">
              Puedes mezclar artículos y servicios; el tipo es informativo.
            </span>
          </label>
          <label className="field">
            <span className="field-label">Emisión</span>
            <input className="input" type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} />
          </label>
          <label className="field">
            <span className="field-label">Vencimiento</span>
            <input className="input" type="date" value={vencimiento} onChange={(e) => setVencimiento(e.target.value)} />
          </label>
          <label className="field">
            <span className="field-label">Tasa VES/USD</span>
            <input className="input" value={tasa} onChange={(e) => setTasa(e.target.value)} />
          </label>
        </div>

        <div className="line-items">
          <div className="line-items__head">
            <strong>Ítems de compra</strong>
            <button type="button" className="btn btn--outline btn--sm" onClick={() => setLineas((c) => [...c, { ...EMPTY_LINE }])}>
              <PlusIcon size={15} /> Agregar línea
            </button>
          </div>
          {lineas.map((line, index) => (
            <div className="item-row" key={index}>
              <SearchSelect
                value={line.productoId}
                onChange={(value) => selectProduct(index, value)}
                options={productos.map((producto) => ({
                  value: producto.id,
                  label: producto.nombre,
                  hint: `${producto.sku} · ${producto.tipo === 'ARTICULO' ? 'Artículo' : 'Servicio'}`,
                }))}
                placeholder="Gasto / producto…"
                ariaLabel="Producto"
                className="item-row__product"
              />
              <input className="input item-row__qty" type="number" min="0.01" value={line.cantidad} onChange={(e) => updateLine(index, { cantidad: e.target.value })} aria-label="Cantidad" />
              <input className="input item-row__price" type="number" min="0" value={line.precio} onChange={(e) => updateLine(index, { precio: e.target.value })} aria-label="Costo Bs." placeholder="Costo Bs." />
              <label className="checkbox">
                <input type="checkbox" checked={line.exenta} onChange={(e) => updateLine(index, { exenta: e.target.checked })} />
                Exenta
              </label>
              <button type="button" className="icon-button icon-button--tiny" aria-label="Eliminar línea" onClick={() => setLineas((c) => (c.length > 1 ? c.filter((_, i) => i !== index) : c))}>
                <TrashIcon size={16} />
              </button>
            </div>
          ))}
          <div className="line-items__totals">
            <span>Base {formatMoney(totals.base, 'VES')}</span>
            <span>IVA {formatMoney(totals.tax, 'VES')}</span>
            <strong>Total {formatMoney(totals.total, 'VES')}</strong>
          </div>
        </div>
        <label className="checkbox">
          <input type="checkbox" checked={exenta} onChange={(e) => setExenta(e.target.checked)} />
          Factura totalmente exenta de IVA
        </label>

        <div className="line-items">
          <div className="line-items__head">
            <strong>Retenciones de la compra</strong>
            <span className="pill pill--soft">Se descuentan al pagar</span>
          </div>
          <div className="form-grid form-grid--3">
            <label className="field">
              <span className="field-label">ISLR (%)</span>
              <input
                className="input"
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={islrPctInput ?? String(proveedorSeleccionado?.islrPct ?? 0)}
                onChange={(e) => setIslrPctInput(e.target.value)}
              />
              <span className="field-hint">Sugerido por el proveedor.</span>
            </label>
            <label className="field">
              <span className="field-label">Retención IVA (%)</span>
              <input
                className="input"
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={ivaRetPct}
                onChange={(e) => setIvaRetPct(e.target.value)}
              />
              <span className="field-hint">Normalmente 75% del IVA.</span>
            </label>
            <div className="field">
              <span className="field-label">Neto a pagar</span>
              <span className="field-static">{formatMoney(netoAPagar, 'VES')}</span>
              <span className="field-hint">
                ISLR {formatMoney(islrEstimado, 'VES')} · IVA ret. {formatMoney(ivaRetenido, 'VES')}
              </span>
            </div>
          </div>
        </div>
        <p className="page-footnote">
          Los artículos actualizan el costo promedio y el kardex; los servicios van directo al
          gasto. El asiento debita inventario o gasto según cada línea, más el IVA crédito, y
          acredita cuentas por pagar. Las retenciones quedan previstas aquí y se aplican al
          registrar el pago, cuando se emite el comprobante de retención.
        </p>
      </div>
    </Modal>
  );
}

function PagoProveedorForm({
  onClose,
  onCreated,
  initialProveedorId,
  initialCompraId,
}: {
  onClose: () => void;
  onCreated: (numero: string) => void;
  initialProveedorId?: string;
  initialCompraId?: string;
}) {
  const { data: proveedores } = useProveedores('');
  const { data: bancos } = useBancos();
  const { notify } = useToast();
  const crear = useRegistrarPagoProveedor();
  const [proveedorId, setProveedorId] = useState(initialProveedorId ?? '');
  const [fecha, setFecha] = useState(today);
  const [bancoId, setBancoId] = useState('');
  const moneda = 'VES' as const;
  const [tasa, setTasa] = useState('36.42');
  const [referencia, setReferencia] = useState('');
  const tasaDia = useTasaDia(fecha);

  useEffect(() => {
    const rate = tasaDia.data?.data.find((item) => item.moneda === 'USD');
    if (rate) setTasa(String(rate.tasa));
  }, [tasaDia.data]);
  const [monto, setMonto] = useState('');
  const [aplicaciones, setAplicaciones] = useState<
    Record<
      string,
      { aplicar: boolean; monto: string; islr: string; iva: string; pct: string; ivaPct: string }
    >
  >({});
  const pendientes = useComprasPendientes(proveedorId || undefined);
  const purchases = pendientes.data ?? [];

  const proveedorSeleccionado = (proveedores?.data ?? []).find((p) => p.id === proveedorId);
  const islrPct = proveedorSeleccionado?.islrPct ?? 0;

  /** Retención proporcional al saldo pendiente, no al total original. */
  const sugerenciaRetencion = (purchase: {
    tasa?: number;
    saldoUsd: number;
    saldoVes?: number;
    baseUsd: number;
    baseVes?: number;
    totalUsd?: number;
    impuestoUsd?: number;
    islrPct?: number;
    retencionIvaPct?: number;
  }) => {
    const rate = purchase.tasa ?? (Number(tasa) || 1);
    const saldoVes = purchase.saldoVes ?? purchase.saldoUsd * rate;
    const totalVes = (purchase.totalUsd ?? 0) * rate;
    const ratio = totalVes > 0 ? Math.min(1, saldoVes / totalVes) : 1;
    const baseVes = (purchase.baseVes ?? purchase.baseUsd * rate) * ratio;
    const ivaVes = (purchase.impuestoUsd ?? 0) * rate * ratio;
    const pct = purchase.islrPct ?? islrPct;
    const ivaPct = purchase.retencionIvaPct ?? 0;
    const islr = Math.round((baseVes * pct) / 100 * 100) / 100;
    const iva = Math.round((ivaVes * ivaPct) / 100 * 100) / 100;
    return { rate, saldoVes, baseVes, ivaVes, pct, ivaPct, islr, iva };
  };

  useEffect(() => {
    if (!initialCompraId) return;
    const purchase = purchases.find((item) => item.id === initialCompraId);
    if (!purchase) return;
    setAplicaciones((current) => {
      if (current[purchase.id]) return current;
      const sugerida = sugerenciaRetencion(purchase);
      return {
        ...current,
        [purchase.id]: {
          aplicar: true,
          monto: String(
            Math.max(0, Math.round((sugerida.saldoVes - sugerida.islr - sugerida.iva) * 100) / 100),
          ),
          islr: sugerida.islr > 0 ? String(sugerida.islr) : '',
          iva: sugerida.iva > 0 ? String(sugerida.iva) : '',
          pct: String(sugerida.pct),
          ivaPct: String(sugerida.ivaPct),
        },
      };
    });
  }, [initialCompraId, purchases, islrPct, tasa]);

  const appliedTotal = useMemo(
    () => Object.values(aplicaciones).filter((a) => a.aplicar).reduce((sum, a) => sum + (Number(a.monto) || 0), 0),
    [aplicaciones],
  );
  const defaultCash = appliedTotal;

  const submit = async () => {
    if (!proveedorId) {
      notify('Selecciona un proveedor.', 'warning');
      return;
    }
    const allocations = purchases
      .filter((purchase) => aplicaciones[purchase.id]?.aplicar)
      .map((purchase) => ({
        compraId: purchase.id,
        montoAplicadoUsd: (Number(aplicaciones[purchase.id]?.monto) || 0) / (purchase.tasa ?? (Number(tasa) || 1)),
        retencionIslrUsd: (Number(aplicaciones[purchase.id]?.islr) || 0) / (purchase.tasa ?? (Number(tasa) || 1)),
        retencionIslrPct: Number(aplicaciones[purchase.id]?.pct) || 0,
        retencionIvaUsd: (Number(aplicaciones[purchase.id]?.iva) || 0) / (purchase.tasa ?? (Number(tasa) || 1)),
      }));
    if (allocations.length === 0) {
      notify('Selecciona al menos una compra pendiente.', 'warning');
      return;
    }
    const cash = Number(monto) || defaultCash;
    try {
      const result = await crear.mutateAsync({
        proveedorId,
        fecha,
        moneda,
        monto: cash,
        tasa: Number(tasa),
        ...(bancoId ? { bancoCuentaId: bancoId } : {}),
        ...(referencia ? { referencia } : {}),
        idempotencyKey: crypto.randomUUID(),
        aplicaciones: allocations,
      });
      onCreated(String((result.data as { numero?: string }).numero ?? ''));
    } catch (error) {
      notify(error instanceof Error ? error.message : 'No se pudo registrar el pago.', 'error');
    }
  };

  return (
    <Modal
      title="Pago a proveedor"
      eyebrow="CxP"
      onClose={onClose}
      wide
      footer={
        <>
          <button type="button" className="btn btn--outline" onClick={onClose}>Cancelar</button>
          <button type="button" className="btn btn--primary" onClick={() => void submit()} disabled={crear.isPending}>
            {crear.isPending ? 'Registrando…' : 'Registrar pago'}
          </button>
        </>
      }
    >
      <div className="modal-card__body">
        <div className="form-grid form-grid--3">
          <label className="field">
            <span className="field-label">Proveedor *</span>
            <SearchSelect
              value={proveedorId}
              onChange={(value) => {
                setProveedorId(value);
                setAplicaciones({});
              }}
              options={(proveedores?.data ?? []).map((proveedor) => ({
                value: proveedor.id,
                label: proveedor.nombre,
                hint: proveedor.rif,
              }))}
              placeholder="Buscar proveedor por nombre o RIF"
              ariaLabel="Proveedor"
            />
          </label>
          <label className="field">
            <span className="field-label">Fecha</span>
            <input className="input" type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} />
          </label>
          <label className="field">
            <span className="field-label">Cuenta bancaria</span>
            <SearchSelect
              value={bancoId}
              onChange={setBancoId}
              options={[
                { value: '', label: 'Selecciona…' },
                ...(bancos ?? []).map((banco) => ({
                  value: banco.id,
                  label: banco.banco,
                  hint: `${banco.moneda} · ${formatMoney(banco.saldoActual, banco.moneda)}`,
                })),
              ]}
              placeholder="Buscar cuenta bancaria"
              ariaLabel="Cuenta bancaria"
            />
          </label>
          <div className="field">
            <span className="field-label">Moneda</span>
            <span className="field-static">Bolívares (Bs.)</span>
          </div>
          <label className="field">
            <span className="field-label">Tasa VES/USD</span>
            <input className="input" value={tasa} onChange={(e) => setTasa(e.target.value)} />
          </label>
          <label className="field">
            <span className="field-label">Monto efectivo (Bs.)</span>
            <input className="input" inputMode="decimal" value={monto} placeholder={String(defaultCash)} onChange={(e) => setMonto(e.target.value)} />
          </label>
          <label className="field">
            <span className="field-label">Referencia</span>
            <input className="input" value={referencia} onChange={(e) => setReferencia(e.target.value)} />
          </label>
        </div>

        <p className="inline-note">
          Las compras de servicios aparecen en esta misma lista. Marca la factura, ajusta el
          efectivo y la retención de ISLR si corresponde, y registra el pago.
        </p>

        <div className="line-items">
          <div className="line-items__head">
            <strong>Compras pendientes</strong>
          </div>
          {!proveedorId ? <p className="muted">Selecciona un proveedor.</p> : null}
          {proveedorId && purchases.length === 0 ? (
            <p className="muted">Este proveedor no tiene compras pendientes de pago.</p>
          ) : null}
          <div className="allocation-list">
            {purchases.map((purchase) => {
              const sugerida = sugerenciaRetencion(purchase);
              const { rate: purchaseRate, baseVes, saldoVes } = sugerida;
              const state = aplicaciones[purchase.id] ?? {
                aplicar: false,
                monto: String(
                  Math.max(
                    0,
                    Math.round((sugerida.saldoVes - sugerida.islr - sugerida.iva) * 100) / 100,
                  ),
                ),
                islr: sugerida.islr > 0 ? String(sugerida.islr) : '',
                iva: sugerida.iva > 0 ? String(sugerida.iva) : '',
                pct: String(sugerida.pct),
                ivaPct: String(sugerida.ivaPct),
              };
              return (
                <div className={`allocation-row${state.aplicar ? ' allocation-row--active' : ''}`} key={purchase.id}>
                  <label className="checkbox">
                    <input
                      type="checkbox"
                      checked={state.aplicar}
                      onChange={() =>
                        setAplicaciones((current) => ({
                          ...current,
                          [purchase.id]: { ...state, aplicar: !state.aplicar },
                        }))
                      }
                    />
                    <span>
                      <strong>{purchase.numeroFactura}</strong>
                      <small>{purchase.numeroInterno} · saldo {formatMoney(purchase.saldoVes ?? purchase.saldoUsd * purchaseRate, 'VES')}</small>
                    </span>
                  </label>
                  <input
                    className="input allocation-row__amount"
                    value={state.monto}
                    onChange={(e) => setAplicaciones((c) => ({ ...c, [purchase.id]: { ...state, monto: e.target.value } }))}
                    aria-label="Efectivo aplicado"
                  />
                  <div className="allocation-row__islr">
                    <input
                      className="input allocation-row__pct"
                      inputMode="decimal"
                      placeholder="%"
                      value={state.pct}
                      onChange={(e) => {
                        const pct = e.target.value;
                        const islr = Math.round((baseVes * (Number(pct) || 0)) / 100 * 100) / 100;
                        const iva = Number(state.iva) || 0;
                        const applied = Math.max(0, saldoVes - islr - iva);
                        setAplicaciones((c) => ({
                          ...c,
                          [purchase.id]: {
                            ...state,
                            pct,
                            islr: islr > 0 ? String(islr) : '',
                            monto: String(Math.round(applied * 100) / 100),
                          },
                        }));
                      }}
                      aria-label={`Porcentaje ISLR de ${purchase.numeroFactura}`}
                    />
                    <span className="allocation-row__pct-sign" aria-hidden="true">%</span>
                    <input
                      className="input allocation-row__amount"
                      placeholder="ISLR Bs."
                      value={state.islr}
                      onChange={(e) => {
                        const islr = Number(e.target.value) || 0;
                        const iva = Number(state.iva) || 0;
                        const applied = Math.max(0, saldoVes - islr - iva);
                        const pct = baseVes > 0 ? Math.round((islr / baseVes) * 10000) / 100 : 0;
                        setAplicaciones((c) => ({
                          ...c,
                          [purchase.id]: {
                            ...state,
                            islr: e.target.value,
                            pct: String(pct),
                            monto: String(Math.round(applied * 100) / 100),
                          },
                        }));
                      }}
                      aria-label="Retención ISLR"
                    />
                  </div>
                  <div className="allocation-row__islr">
                    <input
                      className="input allocation-row__pct"
                      inputMode="decimal"
                      placeholder="IVA %"
                      value={state.ivaPct}
                      onChange={(e) => {
                        const ivaPct = e.target.value;
                        const ivaRet = Math.round((sugerida.ivaVes * (Number(ivaPct) || 0)) / 100 * 100) / 100;
                        const islr = Number(state.islr) || 0;
                        const applied = Math.max(0, saldoVes - islr - ivaRet);
                        setAplicaciones((c) => ({
                          ...c,
                          [purchase.id]: {
                            ...state,
                            ivaPct,
                            iva: ivaRet > 0 ? String(ivaRet) : '',
                            monto: String(Math.round(applied * 100) / 100),
                          },
                        }));
                      }}
                      aria-label={`Porcentaje de IVA retenido de ${purchase.numeroFactura}`}
                    />
                    <span className="allocation-row__pct-sign" aria-hidden="true">%</span>
                    <input
                      className="input allocation-row__amount"
                      placeholder="IVA ret. Bs."
                      value={state.iva}
                      onChange={(e) => {
                        const ivaValue = Number(e.target.value) || 0;
                        const islr = Number(state.islr) || 0;
                        const applied = Math.max(0, saldoVes - islr - ivaValue);
                        const ivaPct =
                          sugerida.ivaVes > 0
                            ? Math.round((ivaValue / sugerida.ivaVes) * 10000) / 100
                            : 0;
                        setAplicaciones((c) => ({
                          ...c,
                          [purchase.id]: {
                            ...state,
                            iva: e.target.value,
                            ivaPct: String(ivaPct),
                            monto: String(Math.round(applied * 100) / 100),
                          },
                        }));
                      }}
                      aria-label="Retención IVA"
                    />
                  </div>
                </div>
              );
            })}
          </div>
          <div className="line-items__totals">
            <span>Efectivo aplicado {formatMoney(appliedTotal, 'VES')}</span>
            <strong>Monto {formatMoney(Number(monto) || defaultCash, 'VES')}</strong>
          </div>
          <p className="page-footnote">
            Las retenciones se suman al saldo cancelado pero no salen del banco. El asiento
            debita CxP por efectivo + retenciones y acredita banco + retenciones por pagar.
            El comprobante ISLR se asigna automáticamente desde el talonario.
          </p>
        </div>
      </div>
    </Modal>
  );
}
